// src/modules/Blog/index.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../config/supabase';
import { useTranslation } from '../../contexts/LanguageContext';
import { FaCalendar, FaEye, FaArrowRight, FaTag } from 'react-icons/fa';

// Styled Components
const BlogContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #111 0%, #2a2a2a 100%);
  padding: 120px 24px 80px;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  color: white;
  margin-bottom: 16px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 24px;
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.$active ? '#111' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  border: 1px solid ${props => props.$active ? '#111' : '#e5e5e5'};
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$active ? '#333' : '#f5f5f5'};
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PostCard = styled(Link)`
  display: block;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 300ms;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  touch-action: manipulation;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
    }
  }

  @media (hover: none) {
    &:active {
      transform: scale(0.98);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }
  }
`;

const PostImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%);
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  position: relative;
  pointer-events: none;

  @media (max-width: 640px) {
    height: 180px;
  }
`;

const PostCategory = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
`;

const PostContent = styled.div`
  padding: 24px;
  pointer-events: none;
`;

const PostTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostExcerpt = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #999;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    font-size: 12px;
  }
`;

const ReadMoreLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #111;
  transition: gap 200ms;

  ${PostCard}:hover & {
    gap: 10px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  grid-column: 1 / -1;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  font-size: 15px;
  color: #666;
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const LoadingImage = styled.div`
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const LoadingContent = styled.div`
  padding: 24px;
`;

const LoadingLine = styled.div`
  height: ${props => props.$height || '16px'};
  width: ${props => props.$width || '100%'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: ${props => props.$mb || '12px'};
`;

// Categorias estáticas (mesmas do BlogEditor)
const STATIC_CATEGORIES = [
  { slug: 'tecnologia', labelKey: 'tecnologia' },
  { slug: 'industria', labelKey: 'industria' },
  { slug: 'ia', labelKey: 'ia' },
  { slug: 'inovacao', labelKey: 'inovacao' },
  { slug: 'cases', labelKey: 'cases' },
  { slug: 'tutoriais', labelKey: 'tutoriais' },
];

const Blog = () => {
  const { t, language } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Erro ao buscar posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtra apenas categorias que têm pelo menos um post
  const availableCategories = STATIC_CATEGORIES.filter(cat =>
    posts.some(post => post.category === cat.slug)
  );

  const filteredPosts = selectedCategory === 'all'
    ? posts
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryName = (slug) => {
    const translated = t(`blog.categories.${slug}`);
    if (translated !== `blog.categories.${slug}`) {
      return translated;
    }
    return slug;
  };

  // Helper para obter conteúdo traduzido
  const getTranslatedField = (post, field) => {
    if (language === 'en' && post[`${field}_en`]) {
      return post[`${field}_en`];
    }
    if (language === 'es' && post[`${field}_es`]) {
      return post[`${field}_es`];
    }
    return post[field];
  };

  return (
    <BlogContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>{t('blog.pageTitle')}</HeroTitle>
          <HeroSubtitle>
            {t('blog.pageSubtitle')}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <FiltersBar>
          <FilterButton
            $active={selectedCategory === 'all'}
            onClick={() => setSelectedCategory('all')}
          >
            {t('blog.allCategories')}
          </FilterButton>
          {availableCategories.map(category => (
            <FilterButton
              key={category.slug}
              $active={selectedCategory === category.slug}
              onClick={() => setSelectedCategory(category.slug)}
            >
              {getCategoryName(category.slug)}
            </FilterButton>
          ))}
        </FiltersBar>

        {loading ? (
          <LoadingGrid>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <LoadingCard key={i}>
                <LoadingImage />
                <LoadingContent>
                  <LoadingLine $height="12px" $width="60px" />
                  <LoadingLine $height="20px" $width="90%" />
                  <LoadingLine $height="14px" $width="100%" />
                  <LoadingLine $height="14px" $width="80%" />
                </LoadingContent>
              </LoadingCard>
            ))}
          </LoadingGrid>
        ) : filteredPosts.length === 0 ? (
          <EmptyState>
            <EmptyTitle>{t('blog.noPostsTitle')}</EmptyTitle>
            <EmptyText>
              {posts.length === 0
                ? t('blog.noPostsText')
                : t('blog.noPostsCategory')
              }
            </EmptyText>
          </EmptyState>
        ) : (
          <PostsGrid>
            {filteredPosts.map(post => (
              <PostCard key={post.id} to={`/${post.slug}`}>
                <PostImage $image={post.cover_image}>
                  {post.category && (
                    <PostCategory>{getCategoryName(post.category)}</PostCategory>
                  )}
                </PostImage>
                <PostContent>
                  <PostTitle>{getTranslatedField(post, 'title')}</PostTitle>
                  <PostExcerpt>{getTranslatedField(post, 'excerpt')}</PostExcerpt>
                  <PostMeta>
                    <MetaItem>
                      <FaCalendar />
                      {formatDate(post.created_at)}
                    </MetaItem>
                    <MetaItem>
                      <FaEye />
                      {post.views || 0} {t('blog.views')}
                    </MetaItem>
                  </PostMeta>
                  <ReadMoreLink>
                    {t('blog.readMore')} <FaArrowRight />
                  </ReadMoreLink>
                </PostContent>
              </PostCard>
            ))}
          </PostsGrid>
        )}
      </ContentSection>
    </BlogContainer>
  );
};

export default Blog;
